import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type OnDemandSpecializationsV1 from 'bundles/naptimejs/resources/onDemandSpecializations.v1';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import { getS12nVariantLabel } from 'bundles/s12n-common/lib/s12nProductVariantUtils';

import _t from 'i18n!nls/enroll-course';

export const getPrePurchaseUpsellCopies = ({
  isStandaloneCourse,
  s12n,
  onSdp,
  productPrice,
  courseraPlusMonthlyPrice,
}: {
  isStandaloneCourse: boolean;
  s12n?: OnDemandSpecializationsV1;
  onSdp: boolean;
  productPrice: ProductPrices;
  courseraPlusMonthlyPrice: ProductPrices;
}): {
  heading: string | React.JSX.Element;
  description: React.JSX.Element;
  courseraPlusMonthlyPriceDisplay: React.JSX.Element;
} => {
  let heading: string | React.JSX.Element;
  let description: React.JSX.Element;
  const courseraPlusMonthlyPriceDisplay = (
    <ReactPriceDisplay
      currency={courseraPlusMonthlyPrice.currencyCode}
      value={courseraPlusMonthlyPrice.amount}
      hideCurrencyCode={true}
    />
  );

  const upsellPriceDifference = courseraPlusMonthlyPrice.amount - productPrice.amount;
  const upsellPriceDifferenceDisplay = (
    <ReactPriceDisplay
      currency={courseraPlusMonthlyPrice.currencyCode}
      value={upsellPriceDifference}
      hideCurrencyCode={true}
    />
  );
  if (courseraPlusMonthlyPrice.amount < productPrice.amount) {
    heading = _t('Get more courses for a lower monthly price');
    description = (
      <FormattedMessage
        message={_t(
          isStandaloneCourse
            ? 'For the recurring monthly price of {monthlyPrice}, you can access this course, plus thousands more from Google, Adobe, IBM, Microsoft, and more.'
            : 'For the monthly price of {monthlyPrice}, you can access this {productType}, plus thousands more from Google, Adobe, IBM, Microsoft, and more.'
        )}
        monthlyPrice={courseraPlusMonthlyPriceDisplay}
        productType={onSdp ? getS12nVariantLabel(s12n?.productVariant) : _t('course')}
      />
    );
  } else if (courseraPlusMonthlyPrice.amount > productPrice.amount) {
    heading = (
      <FormattedMessage
        message={_t('Get more courses for just {upsellPriceDifference} more')}
        upsellPriceDifference={upsellPriceDifferenceDisplay}
      />
    );
    description = (
      <FormattedMessage
        message={_t(
          'For just {upsellPriceDifference} more a month, you can access this {productType}, plus thousands more from Google, Adobe, IBM, Microsoft, and more.'
        )}
        upsellPriceDifference={upsellPriceDifferenceDisplay}
        productType={onSdp ? getS12nVariantLabel(s12n?.productVariant) : _t('course')}
      />
    );
  } else {
    heading = _t('Get more courses for the same price');
    description = (
      <FormattedMessage
        message={_t(
          isStandaloneCourse
            ? 'For the recurring monthly price of {monthlyPrice}, you can access this course, plus thousands more from Google, Adobe, IBM, Microsoft, and more.'
            : 'For the same monthly price of {monthlyPrice}, you can access this {productType}, plus thousands more from Google, Adobe, IBM, Microsoft, and more.'
        )}
        monthlyPrice={courseraPlusMonthlyPriceDisplay}
        productType={onSdp ? getS12nVariantLabel(s12n?.productVariant) : _t('course')}
      />
    );
  }

  return {
    heading,
    description,
    courseraPlusMonthlyPriceDisplay,
  };
};
